import AppLayout from '@cloudscape-design/components/app-layout';
import BreadcrumbGroup, { type BreadcrumbGroupProps } from '@cloudscape-design/components/breadcrumb-group';
import { applyMode, Mode } from '@cloudscape-design/global-styles';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { GlobalFlashbar, NotificationProvider } from './notifications/NotificationProvider.js';
import { HeaderSearch } from './search/index.js';
import { SectionSlot } from './slot/SectionSlot.js';
import { SideNav } from './SideNav.js';
import { TopNav } from './TopNav.js';

const PREFS_KEY = 'vex.console.prefs';

interface Prefs {
  theme: 'light' | 'dark';
  density: 'comfortable' | 'compact';
}

const DEFAULT_PREFS: Prefs = { theme: 'light', density: 'comfortable' };

function loadPrefs(): Prefs {
  if (typeof window === 'undefined') return DEFAULT_PREFS;
  const raw = window.localStorage.getItem(PREFS_KEY);
  if (!raw) return DEFAULT_PREFS;
  try {
    return { ...DEFAULT_PREFS, ...(JSON.parse(raw) as Partial<Prefs>) };
  } catch {
    return DEFAULT_PREFS;
  }
}

function savePrefs(p: Prefs) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(PREFS_KEY, JSON.stringify(p));
}

export interface AppShellProps {
  productName?: string;
  /** Optional content slot. When omitted, react-router's `<Outlet />` is used. */
  children?: ReactNode;
}

export function AppShell({ productName, children }: AppShellProps) {
  const [prefs, setPrefs] = useState<Prefs>(loadPrefs);

  useEffect(() => {
    applyMode(prefs.theme === 'dark' ? Mode.Dark : Mode.Light);
    document.body.classList.toggle('awsui-compact-mode', prefs.density === 'compact');
    savePrefs(prefs);
  }, [prefs]);

  // Move the TopNavigation search slot from its default middle position to
  // the left, immediately after the identity. Cloudscape doesn't expose a
  // layout prop for this — we collapse the search wrapper to content-width
  // and push utilities to the far right via `margin-left: auto`. The class
  // selectors use `[class*=...]` because Cloudscape emits hashed CSS-module
  // class names, but the module prefixes are stable across releases.
  useEffect(() => {
    const styleId = 'vex-topnav-search-left';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      [class*="awsui_search-slot_"] {
        flex: 0 0 auto !important;
        max-width: 420px;
        width: 420px;
        margin-left: 12px;
      }
      [class*="awsui_inputs_"] {
        flex: 0 0 auto !important;
        margin-right: auto !important;
      }
      [class*="awsui_utilities_"] {
        margin-left: auto !important;
      }
    `;
    document.head.appendChild(style);
  }, []);

  const toggleTheme = useCallback(
    () => setPrefs((p) => ({ ...p, theme: p.theme === 'dark' ? 'light' : 'dark' })),
    [],
  );
  const toggleDensity = useCallback(
    () =>
      setPrefs((p) => ({
        ...p,
        density: p.density === 'compact' ? 'comfortable' : 'compact',
      })),
    [],
  );

  return (
    <NotificationProvider>
      <TopNav
        {...(productName ? { productName } : {})}
        search={<HeaderSearch />}
        onToggleTheme={toggleTheme}
        onToggleDensity={toggleDensity}
        theme={prefs.theme}
      />
      <AppLayout
        navigation={<SideNav />}
        toolsHide
        notifications={<GlobalFlashbar />}
        breadcrumbs={<Breadcrumbs />}
        content={<SectionSlot>{children ?? <Outlet />}</SectionSlot>}
      />
    </NotificationProvider>
  );
}

function Breadcrumbs() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const parts = pathname.split('/').filter(Boolean);
  const items: BreadcrumbGroupProps.Item[] = [
    { text: 'Console', href: '/' },
    ...parts.map((p, i) => ({ text: p, href: '/' + parts.slice(0, i + 1).join('/') })),
  ];
  return (
    <BreadcrumbGroup
      items={items}
      onFollow={(e) => {
        e.preventDefault();
        navigate(e.detail.href);
      }}
    />
  );
}
